<template>
	<div class="pptx-viewer">
		<div v-if="loading" class="pptx-viewer__loading">
			<div class="pptx-viewer__spinner"></div>
			<p>Загрузка презентации...</p>
		</div>
		
		<div v-else-if="error" class="pptx-viewer__error-notice">
			<div class="pptx-viewer__notice-icon">⚠️</div>
			<p class="pptx-viewer__notice-title">Не удалось загрузить презентацию</p>
			<p class="pptx-viewer__notice-text">{{ error }}</p>
			<div class="pptx-viewer__actions">
				<a
					:href="fileUrl"
					download
					class="pptx-viewer__download-btn"
				>
					Скачать презентацию
				</a>
			</div>
		</div>
		
		<iframe
			v-else
			:src="viewerUrl"
			class="pptx-viewer__frame"
			frameborder="0"
			allowfullscreen
			@load="handleLoad"
		></iframe>
	</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

const props = defineProps({
	fileUrl: {
		type: String,
		required: true
	}
})

const loading = ref(true)
const error = ref(null)

const viewerUrl = computed(() => {
	// Используем Google Docs Viewer - работает с любыми файлами
	const encodedUrl = encodeURIComponent(props.fileUrl)
	return `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`
})

const handleLoad = () => {
	loading.value = false
	
	// Проверяем через таймаут, загрузился ли контент
	setTimeout(() => {
		const iframe = document.querySelector('.pptx-viewer__frame')
		if (iframe) {
			try {
				// Если iframe пустой - показываем ошибку
				const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
				if (iframeDoc && iframeDoc.body && iframeDoc.body.innerHTML.length < 100) {
					error.value = 'Не удалось загрузить презентацию через Google Viewer'
				}
			} catch (e) {
				// CORS - это нормально, значит загрузилось
			}
		}
	}, 3000)
}

onMounted(() => {
	// Таймаут на случай если iframe не загрузится
	setTimeout(() => {
		if (loading.value) {
			loading.value = false
			error.value = 'Превышено время ожидания загрузки'
		}
	}, 15000)
})
</script>

<style scoped>
.pptx-viewer {
	width: 100%;
	height: 100%;
	min-height: 60vh;
	display: flex;
	flex-direction: column;
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
	overflow: hidden;
}

.pptx-viewer__loading {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	padding: 60px 40px;
}

.pptx-viewer__spinner {
	width: 48px;
	height: 48px;
	border: 4px solid #e2e8f0;
	border-top-color: #3b82f6;
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

@keyframes spin {
	to { transform: rotate(360deg); }
}

.pptx-viewer__error-notice {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	padding: 60px 40px;
	background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
	border: 2px solid #f87171;
	border-radius: 16px;
	margin: 20px;
}

.pptx-viewer__notice-icon {
	font-size: 64px;
	opacity: 0.8;
}

.pptx-viewer__notice-title {
	font-size: 20px;
	font-weight: 600;
	color: #dc2626;
	margin: 0;
	text-align: center;
}

.pptx-viewer__notice-text {
	font-size: 14px;
	color: #991b1b;
	margin: 0;
	max-width: 500px;
	line-height: 1.6;
	text-align: center;
}

.pptx-viewer__actions {
	display: flex;
	gap: 12px;
	margin-top: 12px;
}

.pptx-viewer__download-btn {
	background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
	color: #ffffff;
	padding: 14px 28px;
	border-radius: 10px;
	font-size: 15px;
	font-weight: 600;
	text-decoration: none;
	transition: transform 0.2s ease, box-shadow 0.2s ease;
	display: inline-block;
}

.pptx-viewer__download-btn:hover {
	transform: translateY(-2px);
	box-shadow: 0 8px 20px rgba(249, 115, 22, 0.3);
}

.pptx-viewer__frame {
	width: 100%;
	height: 100%;
	min-height: 60vh;
	border: none;
}
</style>
